import { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import useAlert from "../../../../hooks/useAlert";
import {
  PatientDetailsFormInputs,
  UserStatus,
} from "../../../../models/users.interface";
import usersService from "../../../../services/users.service";
import AlertModal from "../../../Shared/AlertModal/AlertModal";
import CancelButton from "../../../Shared/CancelButton/CancelButton";
import Card from "../../../Shared/Card/Card";
import SaveButton from "../../../Shared/SaveButton/SaveButton";
import TextFormInput from "../../../Shared/TextInput/TextFormInput";
import PatientInfoInputs from "../PatientInfoInputs/PatientInfoInputs";
import PatientOrgInfoInputs from "../PatientOrgInfoInputs/PatientOrgInfoInputs";
import PatientPermissionsInputs from "../PatientPermissionsInputs/PatientPermissionsInputs";
import PatientGeneralDetailsHeader from "./PatientGeneralDetailsHeader/PatientGeneralDetailsHeader";
import styles from "./PatientGeneralDetails.module.css";
import { getFormChangedData } from "../../../../utils/formUtil";
import { DEFAULT_PHONE_COUNTRY_CODE } from "../../../../constants";

type PatientGeneralDetailsProps = {
  userId: string;
  createdAt?: string;
  activatedAt?: string;
  userStatus?: UserStatus;
  totalEcgs?: number;
  lastEcgDate?: string;
  deviceSn?: string;
  formData?: PatientDetailsFormInputs;
  totalProfiles?: number;

  onUpdated?: () => void;
  onCancel?: () => void;
  onToggleUserActive?: (isActive: boolean) => void;
};

const PatientGeneralDetails = ({
  userId,
  formData,
  totalProfiles,
  onUpdated,
  onCancel,
  ...restOfProps
}: PatientGeneralDetailsProps) => {
  const formMethods = useForm<PatientDetailsFormInputs>({
    defaultValues: {
      countryCode: formData?.countryCode || DEFAULT_PHONE_COUNTRY_CODE,
    },
  });
  const [loading, setLoading] = useState(false);
  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();
  const intl = useIntl();
  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors: formErrors, dirtyFields },
    control,
    getValues,
  } = formMethods;

  useEffect(() => {
    if (!userId || !formData) return;

    reset(formData);
  }, [userId, formData, reset]);

  const saveHandler = async () => {
    setLoading(true);

    const changedData = getFormChangedData<PatientDetailsFormInputs>(
      dirtyFields,
      getValues
    );

    try {
      const { success, errorCode } = await usersService.updatePatient({
        id: userId,
        ...changedData,
      });

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.UPDATE_PATIENT",
          "An error occurred while updating patient information.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      if (onUpdated) onUpdated();
    } finally {
      setLoading(false);
    }
  };

  const headerProps = { ...restOfProps, loading };
  const textPlaceholder = intl.messages["GLOBAL.INSERT_HERE"] as string;

  return (
    <div className="h-100 d-flex flex-column">
      <PatientGeneralDetailsHeader {...headerProps} />
      <FormProvider {...formMethods}>
        <Form className="d-flex flex-column flex-grow-1">
          <div className="flex-grow-1 p-2">
            <Card className="mb-2 p-3">
              <PatientOrgInfoInputs
                disabled={loading}
                entityReadonly={!!totalProfiles}
              />
            </Card>
            <Card className="mb-2 p-3">
              <PatientInfoInputs
                disabled={loading}
                isEdit
                defaultCountryCode={getValues("countryCode")}
              />
            </Card>
            <Card className="mb-2 p-3">
              <Form.Label className="mb-1 font-md">
                <FormattedMessage
                  id="USER.REFERENCE"
                  defaultMessage="Reference"
                />
                :
              </Form.Label>
              <TextFormInput<PatientDetailsFormInputs>
                name={"reference"}
                control={control}
                input={{
                  isInvalid: !!formErrors?.reference,
                  type: "textarea",
                  textareaRows: 4,
                  placeholder: textPlaceholder,
                  disabled: loading,
                }}
              />
            </Card>
            <Card className="mb-2 p-3">
              <div className="p-2">
                <PatientPermissionsInputs disabled={loading} />
              </div>
            </Card>
          </div>
          <footer className={`${styles.footer} p-3 d-flex justify-content-end`}>
            <CancelButton onClick={onCancel} disabled={loading} />
            <span className="mx-2"></span>
            <SaveButton
              onClick={handleSubmit(saveHandler)}
              className="prim-bg-gradient-color"
              disabled={!isDirty || loading}
            >
              {loading && (
                <>
                  <FormattedMessage id="GLOBAL.SAVE" defaultMessage="Save" />
                  &nbsp;&nbsp;
                  <Spinner as="span" animation="border" size="sm" />
                </>
              )}
            </SaveButton>
          </footer>
        </Form>
      </FormProvider>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </div>
  );
};

export default PatientGeneralDetails;
